import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { consultationFaqs } from '@/mocks/consultationData';

export default function FAQSection() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: consultationFaqs.map((_faq, idx) => ({
      '@type': 'Question',
      name: t(`consultation.faq.${idx}.question`),
      acceptedAnswer: {
        '@type': 'Answer',
        text: t(`consultation.faq.${idx}.answer`),
      },
    })),
  };

  return (
    <section className="relative py-20 md:py-28 bg-background-100/50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      <div className="max-w-[800px] mx-auto px-8">
        <div className="text-center mb-14">
          <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">
            {t('consultation.faq.label')}
          </p>
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('consultation.faq.title')}
          </h2>
          <p className="text-accent-500 text-[15px] tracking-[0.12em] mb-4 font-label">
            {t('consultation.faq.titleEn')}
          </p>
          <div className="w-10 h-[1px] bg-accent-500/40 mx-auto"></div>
        </div>

        <div className="space-y-3">
          {consultationFaqs.map((_faq, idx) => (
            <div
              key={idx}
              className="border border-background-200/60 bg-background-50"
            >
              <button
                onClick={() => toggle(idx)}
                className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
              >
                <span className="text-[16px] text-foreground-800 tracking-[0.04em] font-medium pr-4">
                  {t(`consultation.faq.${idx}.question`)}
                </span>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`text-accent-500 text-sm transition-transform duration-300 ${
                      openIndex === idx ? 'ri-subtract-line' : 'ri-add-line'
                    }`}
                  ></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === idx ? 'max-h-[300px]' : 'max-h-0'
                }`}
              >
                <p className="px-5 pb-5 text-[15px] text-foreground-500 tracking-[0.04em] leading-relaxed">
                  {t(`consultation.faq.${idx}.answer`)}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-[14px] text-foreground-400 tracking-[0.04em] mt-8">
          {t('consultation.faq.contactNote')}
          <a href="/contact" className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors mx-1">
            {t('consultation.faq.contactLink')}
          </a>
          {t('consultation.faq.contactSuffix')}
        </p>
      </div>
    </section>
  );
}
